import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type { Flag, ImpressionsSummary } from "@spoonity/flags-contract";
import { getImpressionsSummary, listFlags } from "../api/endpoints";
import { ImpressionsBarChart } from "../components/BarChart";
import { EmptyState } from "../components/EmptyState";
import { useToast } from "../components/Toast";
import { useWorkspaceEnv } from "../state/WorkspaceEnvContext";

type Range = "24h" | "7d" | "30d";

function rangeToFromTo(range: Range): { from: string; to: string } {
  const to = new Date();
  const from = new Date(to);
  if (range === "24h") from.setHours(from.getHours() - 24);
  else if (range === "7d") from.setDate(from.getDate() - 7);
  else from.setDate(from.getDate() - 30);
  return { from: from.toISOString(), to: to.toISOString() };
}

/** Impressions summary per flag: range selector + hand-drawn SVG chart (task brief). */
export function ImpressionsPage() {
  const { workspace, environment } = useWorkspaceEnv();
  const { pushError } = useToast();
  const [params, setParams] = useSearchParams();
  const [flags, setFlags] = useState<Flag[]>([]);
  const [flagKey, setFlagKey] = useState(params.get("flag") ?? "");
  const [range, setRange] = useState<Range>("24h");
  const [summary, setSummary] = useState<ImpressionsSummary | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!workspace) return;
    let cancelled = false;
    (async () => {
      try {
        const page = await listFlags(workspace.id, { limit: 200 });
        if (cancelled) return;
        setFlags(page.items);
        if (!flagKey && page.items[0]) setFlagKey(page.items[0].key);
      } catch (err) {
        if (!cancelled) pushError(err, "Could not load flags");
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspace]);

  useEffect(() => {
    if (!environment || !flagKey) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { from, to } = rangeToFromTo(range);
        const s = await getImpressionsSummary(environment.id, { flag: flagKey, from, to });
        if (!cancelled) setSummary(s);
      } catch (err) {
        if (!cancelled) pushError(err, "Could not load impressions");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [environment, flagKey, range, pushError]);

  if (!environment) return null;

  return (
    <div>
      <div className="row wrap" style={{ marginBottom: "var(--space-4)" }}>
        <select
          className="select"
          value={flagKey}
          onChange={(e) => {
            setFlagKey(e.target.value);
            setParams({ flag: e.target.value });
          }}
          aria-label="Flag"
        >
          {flags.map((f) => (
            <option key={f.key} value={f.key}>
              {f.name}
            </option>
          ))}
        </select>
        <select className="select" value={range} onChange={(e) => setRange(e.target.value as Range)} aria-label="Range">
          <option value="24h">Last 24 hours</option>
          <option value="7d">Last 7 days</option>
          <option value="30d">Last 30 days</option>
        </select>
      </div>

      {loading && <p className="muted">Loading…</p>}
      {!loading && summary && summary.total === 0 && <EmptyState title="No impressions in this range" />}
      {!loading && summary && summary.total > 0 && (
        <div className="card">
          <p>
            <strong>{summary.total}</strong> impressions between {new Date(summary.from).toLocaleString()} and{" "}
            {new Date(summary.to).toLocaleString()}
          </p>
          <ImpressionsBarChart buckets={summary.buckets} />
        </div>
      )}
    </div>
  );
}
